import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const QuotaHelperPanel = ({ dimensions, cells, projectStatus, assignmentStatus, userRole, pendingModifications = [] }) => {
  // Calculate allocated quota for each dimension value
  // Uses pending target values when available (so pending decreases free up quota)
  const getAllocatedQuota = (dimensionId, valueCode) => {
    return cells
      .filter(c => {
        const cellValues = c.dimension_values_json || {};
        return cellValues[dimensionId] === valueCode;
      })
      .reduce((sum, c) => {
        // Pending deletion = count as 0
        const hasPendingDelete = pendingModifications.some(
          pm => Number(pm.quota_cell_id) === Number(c.id) && pm.field === 'delete'
        );
        if (hasPendingDelete) return sum;
        // Pending target change = use new value
        const pendingMod = pendingModifications.find(
          pm => Number(pm.quota_cell_id) === Number(c.id) && pm.field === 'target_count'
        );
        const effectiveTarget = pendingMod ? (parseInt(pendingMod.new_value) || 0) : (c.target_count || 0);
        return sum + effectiveTarget;
      }, 0);
  };

  // Get available quota for a dimension value (target_quota - allocated)
  const getAvailableQuota = (dimensionId, valueCode) => {
    const dimension = dimensions.find(d => d.id === dimensionId);
    if (!dimension) return 0;
    
    const value = dimension.values_json?.find(v => v.code === valueCode);
    if (!value) return 0;
    
    const targetQuota = parseInt(value.target_quota) || 0;
    const allocated = getAllocatedQuota(dimensionId, valueCode);
    
    return Math.max(0, targetQuota - allocated);
  };
  
  // Check if all quotas are exhausted (all green = no quota remaining)
  const checkAllQuotasExhausted = useCallback(() => {
    if (!dimensions || dimensions.length === 0) return false;
    
    for (const dimension of dimensions) {
      const values = dimension.values_json || [];
      const activeValues = values.filter(v => 
        v.is_active !== false && (parseInt(v.target_quota) || 0) > 0
      );
      
      for (const value of activeValues) {
        const available = getAvailableQuota(dimension.id, value.code);
        if (available > 0) {
          return false; // Found at least one value with quota remaining
        }
      }
    }
    
    return true; // All quotas exhausted
  }, [dimensions, cells, pendingModifications]);
  
  const [showQuotaHelper, setShowQuotaHelper] = useState(true);
  
  // Auto-collapse when project is locked/finished/archived
  // or when chef_terrain_superieur is reviewing pending approval
  useEffect(() => {
    if (projectStatus && ['locked', 'finished', 'archived'].includes(projectStatus)) {
      setShowQuotaHelper(false);
    }
    if (userRole === 'chef_terrain_superieur' && assignmentStatus === 'pending_approval') {
      setShowQuotaHelper(false);
    }
  }, [projectStatus, assignmentStatus, userRole]);
  
  // Auto-collapse when all quotas are exhausted
  useEffect(() => {
    if (checkAllQuotasExhausted()) {
      setShowQuotaHelper(false);
    } else {
      // Keep it open if there's quota remaining (user can still manually close it)
      // Don't force it open, just don't auto-close
    }
  }, [checkAllQuotasExhausted]);

  if (!dimensions || dimensions.length === 0) {
    return null;
  }

  return (
    <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200">
      <button
        type="button"
        onClick={() => setShowQuotaHelper(!showQuotaHelper)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-blue-100 transition-colors"
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-gray-800">
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-xs" />
          Aide : Quotas restants par dimension
        </span>
        <FontAwesomeIcon 
          icon={showQuotaHelper ? faChevronUp : faChevronDown} 
          className="text-xs text-gray-600"
        />
      </button>
      
      {showQuotaHelper && (
        <div className="px-4 py-2 max-h-64 overflow-y-auto">
          <div className="flex flex-wrap gap-3">
            {dimensions.map(dimension => {
              const values = dimension.values_json || [];
              const activeValues = values.filter(v => 
                v.is_active !== false && (parseInt(v.target_quota) || 0) > 0
              );
              
              if (activeValues.length === 0) return null;
              
              return (
                <div key={dimension.id} className="bg-white rounded border border-gray-200 p-2 flex-shrink-0" style={{ minWidth: '200px', maxWidth: '300px' }}>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2 pb-1 border-b border-gray-200">
                    {dimension.label}
                  </h4>
                  <div className="space-y-1">
                    {activeValues.map(value => {
                      const available = getAvailableQuota(dimension.id, value.code);
                      const targetQuota = parseInt(value.target_quota) || 0;
                      const allocated = targetQuota - available;
                      const percentageUsed = targetQuota > 0 ? (allocated / targetQuota) * 100 : 0;
                      const isLowQuota = available <= targetQuota * 0.1 && available > 0;
                      const isEmpty = available === 0; // No quota remaining
                      
                      return (
                        <div
                          key={value.code}
                          className={`p-1.5 rounded border text-xs ${
                            isEmpty 
                              ? 'bg-green-50 border-green-200' 
                              : isLowQuota 
                                ? 'bg-yellow-50 border-yellow-200' 
                                : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium text-gray-900 truncate" style={{ maxWidth: '120px' }}>
                              {value.label}
                            </span>
                            <span className={`text-xs font-semibold flex-shrink-0 ml-2 ${
                              isEmpty 
                                ? 'text-green-700' 
                                : isLowQuota 
                                  ? 'text-yellow-700' 
                                  : 'text-red-700'
                            }`}>
                              {available}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>{allocated}/{targetQuota}</span>
                            {targetQuota > 0 && (
                              <div className="flex-1 mx-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full transition-all duration-300 ${
                                    isEmpty 
                                      ? 'bg-green-500' 
                                      : percentageUsed >= 90 
                                        ? 'bg-yellow-500' 
                                        : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(100, percentageUsed)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotaHelperPanel;
